// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import { get } from 'lodash/fp';
import t from 'app/utils/translate';
import * as h2hActions from 'app/actions/h2hActions';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import { loadLineUp } from 'app/actions/challengeActions';
import * as myTeamsActions from 'app/actions/myTeamsActions';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import EditLineUpBuilder from './EditLineupBuilder';

type Props = {
  challengeSlateId?: string,
  errorMessages: Array<string>,
  getMyLineUp: (id: number) => void,
  lineup?: Object,
  lineupId: string,
  lineupUpdated: boolean,
  fetchSlate: (id: string) => void,
  isLineupLoading: boolean,
  isSlateLoading: boolean,
  isUpdating: boolean,
  onLoadLineUp: (selectionHash: string) => void,
  onBack: () => void
};

type State = {
  entryFee: number,
  numContests: number,
  selectedSport: Object,
  selectedSlate: Object,
  pageNo: number
};

const LoadingWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  width: '100%'
});

const Wrapper = styled.div(({ theme }) => ({
  overflow: 'hidden',
  width: '100vw',
  [theme.mediaQuery.desktopAndUp]: {
    width: 'auto'
  }
}));

const MainSection = styled.div(
  {
    flexBasis: '50%',
    transition: '0.2s opacity',
    maxWidth: '100vw'
  },
  ({ theme }) => ({
    [theme.mediaQuery.desktopAndUp]: {
      flexBasis: 'auto'
    }
  })
);

class EditLineup extends React.Component<Props, State> {
  props: Props;

  static defaultProps = {
    challengeSlateId: undefined,
    lineup: undefined
  };

  componentDidMount() {
    const { lineupId, getMyLineUp } = this.props;

    getMyLineUp(lineupId);
  }

  componentDidUpdate(prevProps: Props) {
    const {
      challengeSlateId,
      isLineupLoading,
      isSlateLoading,
      fetchSlate,
      lineup,
      onLoadLineUp
    } = this.props;

    if (!isLineupLoading && prevProps.isLineupLoading && get('slate.id', lineup)) {
      fetchSlate(lineup.slate.id);
    }

    if (!isSlateLoading && prevProps.isSlateLoading && challengeSlateId) {
      onLoadLineUp(lineup.selectionHash);
    }
  }

  render() {
    const {
      lineupId,
      lineup,
      errorMessages,
      isLineupLoading,
      isSlateLoading,
      isUpdating,
      lineupUpdated,
      onBack
    } = this.props;
    const hasError = errorMessages.length > 0;

    if (!lineupId) {
      return (
        <div>
          <Helmet>{t('Edit Lineup')}</Helmet>
          <Wrapper>
            <h2>Please select a lineup</h2>
          </Wrapper>
        </div>
      );
    }

    return (
      <div>
        <Wrapper>
          {(isSlateLoading || isLineupLoading) && (
            <LoadingWrapper>
              <FriendsLoader>Fetching the lineup, just a moment...</FriendsLoader>
            </LoadingWrapper>
          )}
          {!isSlateLoading && !isLineupLoading && (
            <MainSection>
              <EditLineUpBuilder
                challengeName={get('slate.name', lineup)}
                editLineup
                lineupId={lineupId}
                slateId={get('slate.id', lineup)}
                cost={0}
                entryFee={0}
                numChallengesToCreate={0}
                onBack={onBack}
              />
            </MainSection>
          )}
        </Wrapper>
        <NotificationHandler
          afterCond={!isUpdating && lineupUpdated}
          beforeCond={isUpdating}
          type="success"
        >
          <span>
            Lineup updated.
            <br />
          </span>
        </NotificationHandler>
        <NotificationHandler
          afterCond={!isUpdating && hasError}
          beforeCond={isUpdating}
          persist={hasError}
          type="danger"
        >
          <span>
            Failed to update lineup.
            <br />
            {errorMessages.join('\n')}
          </span>
        </NotificationHandler>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    lineup: get('myTeams.lineup', state),
    isLineupLoading: state.myTeams.isLoading,
    isSlateLoading: state.challenge.isLoading,
    isUpdating: state.lineUp.isSubmitting,
    errorMessages: state.lineUp.errorMessages,
    lineupUpdated: state.lineUp.lineupUpdated,
    challengeSlateId: state.challenge.id
  };
}

const actions = {
  fetchSlate: h2hActions.fetchSlate,
  getMyLineUp: myTeamsActions.getMyLineUp,
  onLoadLineUp: loadLineUp
};

export default connect(mapStateToProps, actions)(EditLineup);
